import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import QRCode from 'react-qr-code';

export default function ClientCards() {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer);
  const userId = authState?.user?._id;

  const [viewMode, setViewMode] = useState("list"); 
  const [cards, setCards] = useState([]);
  const [search, setSearch] = useState("");

  const dummyData = [
    { name: "Sanjeev Kumar", organisation: "Think5Whys", email: "sanjeev@think5whys.com", mobile: "9876543210", code: "TU243BM5850150" },
    { name: "Bhavna Patel", organisation: "GrowthPlus", email: "bhavna@gp.com", mobile: "9876500011", code: "TU99887766554433" },
    { name: "Chirag Jain", organisation: "BluePeak", email: "chirag@bp.com", mobile: "9988776655", code: "TU55667788990011" },
    { name: "Divya Sethi", organisation: "NextLeap", email: "divya@nl.com", mobile: "9123456780", code: "TU11112222333344" },
    { name: "Farhan Zaidi", organisation: "InsightHub", email: "farhan@ih.com", mobile: "9000090000", code: "TU44556677889900" },
    { name: "Gauri Iyer", organisation: "ClarityOS", email: "gauri@cos.com", mobile: "9090909090", code: "TUABCDEF12345678" },
    { name: "Harsh Mehta", organisation: "ThinkLogics", email: "harsh@tl.com", mobile: "9988007700", code: "TUFF112233445566" },
    { name: "Jyoti Nair", organisation: "SoulSync", email: "jyoti@ss.com", mobile: "8888777666", code: "TUGG778899001122" },
    { name: "Karan Singh", organisation: "BrightWave", email: "karan@bw.com", mobile: "8080808080", code: "TUZZ112244668899" },
    { name: "Lakshmi Rao", organisation: "Elevate360", email: "lakshmi@e360.com", mobile: "8989898989", code: "TU44558899660011" }
  ];

  const loadMyList = useCallback(() => {
    if (!authState?.token || !userId) return;

    const config = {
      url: "/contactgenious/list",
      method: "post",
      headers: { Authorization: authState.token },
      data: { createdBy: userId }
    };

    axios(config)
      .then((res) => {
        if (res.data?.data?.length > 0) setCards(res.data.data);
        else setCards(dummyData);
      })
      .catch(() => setCards(dummyData));
  }, [authState?.token, userId]);

  useEffect(() => {
    loadMyList();
  }, [loadMyList]);

  const filteredCards = cards
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((c) =>
      [c.name, c.organisation, c.email, c.mobile, c.code]
        .some((field) =>
          field?.toLowerCase().includes(search.toLowerCase())
        )
    );

  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    mobile: "",
    code: ""
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    let err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.organisation.trim()) err.organisation = "Organisation is required";
    if (!form.email.trim()) err.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Invalid email";
    if (!form.mobile.trim()) err.mobile = "Mobile is required";
    else if (!/^\d{10}$/.test(form.mobile)) err.mobile = "Enter 10-digit mobile";
    if (!form.code.trim()) err.code = "Think5Whys Code required";
    else if (!/^TU[A-Za-z0-9]{14}$/.test(form.code)) err.code = "Must start with TU & be 16 chars";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      name: form.name,
      organisation: form.organisation,
      email: form.email,
      mobile: form.mobile,
      think5whysCode: form.code,
      createdBy: userId
    };

    const config = {
      url: "/contactgenious/create",
      method: "post",
      headers: { Authorization: authState.token },
      data: payload
    };

    axios(config)
      .then(() => {
        alert("Card Created");
        loadMyList();
        setForm({ name: "", organisation: "", email: "", mobile: "", code: "" });
        setViewMode("list");
      })
      .catch(() => alert("Error saving card"));
  };

  // ------------------ PDF Download ------------------
  const listRef = useRef(null);

  const downloadListAsPDF = async () => {
  if (!listRef.current) return;

  // Clone the node to modify styles for PDF
  const clonedNode = listRef.current.cloneNode(true);

  // Apply white background, borders, and black text
  clonedNode.querySelectorAll(".bg-gray-800").forEach(el => {
    el.style.background = "#ffffff";       // white background
    el.style.border = "1px solid #000";   // black border
    el.style.color = "#000000";            // black text
  });

  // Force all child text elements to black
  clonedNode.querySelectorAll("*").forEach(el => {
    el.style.color = "#000000";
  });

  document.body.appendChild(clonedNode); // temporarily add to DOM
  const canvas = await html2canvas(clonedNode, { scale: 2 });
  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
  pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
  pdf.save("ClientCardsList.pdf");

  document.body.removeChild(clonedNode); // clean up
};


  return (
    <div className="min-h-screen bg-black text-white flex flex-col">

      {/* Header */}
      <header className="p-4 border-b border-blue-600 text-lg lg:text-xl font-bold bg-black text-yellow-200">
        Think5Whys Contact Genious: Client Cards
      </header>

      <main className="flex-1 p-4">

        {/* Toggle Buttons */}
        <div className="flex gap-4 mb-4">
          <button
            className={`px-4 py-2 rounded ${viewMode === "list" ? "bg-blue-600" : "bg-gray-800"}`}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
          <button
            className={`px-4 py-2 rounded ${viewMode === "add" ? "bg-blue-600" : "bg-gray-800"}`}
            onClick={() => setViewMode("add")}
          >
            Add
          </button>

          {viewMode === "list" && (
            <button
              onClick={downloadListAsPDF}
              className="ml-auto px-4 py-2 bg-green-600 rounded text-white"
            >
              Download List
            </button>
          )}
        </div>

        {/* List View */}
        {viewMode === "list" && (
          <>
            <input
              className="w-full p-2 bg-gray-900 text-white rounded mb-4"
              placeholder="Search by Name / Email / Mobile / Organisation / Code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div ref={listRef} className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {filteredCards.map((card, idx) => (
                <div
                  key={idx}
                  className="bg-gray-800 p-4 rounded shadow hover:shadow-lg cursor-pointer transition flex flex-col items-center"
                  onClick={() => navigate(`/pub/${card.code}`)}
                >
                  <div className="font-bold text-yellow-300">{card.name}</div>
                  <div className="text-gray-300">{card.organisation}</div>
                  <div className="text-gray-400 text-sm">{card.email}</div>
                  <div className="text-gray-400 text-sm">{card.mobile}</div>
                  <div className="text-blue-400 text-sm mb-2">{card.code}</div>

                  {/* QR Code */}
                  <div className="bg-white p-1 rounded-md shadow-md">
                    <QRCode value={`https://www.think5whys.com/pub/${card.code}`} size={64} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Add Form View */}
        {viewMode === "add" && (
          <div className="space-y-3">
            {["name", "organisation", "email", "mobile", "code"].map((field) => (
              <div key={field}>
                <input
                  className="w-full p-2 bg-gray-900 rounded text-white"
                  placeholder={field.toUpperCase()}
                  value={form[field]}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                />
                {errors[field] && (
                  <div className="text-red-400 text-sm">{errors[field]}</div>
                )}
              </div>
            ))}

            <button
              onClick={handleSubmit}
              className="mt-4 px-4 py-2 bg-blue-600 rounded text-white"
            >
              Save Card
            </button>
          </div>
        )}

      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
